/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import * as DashboardCURD from "./DashboardCRUD";
import {getCSS, getCSSVariableValue} from "../../../_metronic/assets/ts/_utils";
import { InvoiceUsageModel } from '../../models/ViettelInvoiceModel';
import { formatPrice } from '../../selectors';
import { UserLoggedIn } from '../../hooks/PermissionChecker';

type Props = {
  className: string
  colorBase: string
  store?: string
}

const InvoiceCharts: React.FC<Props> = ({className, colorBase, store}) => {
  const pieChartRef = useRef<HTMLDivElement | null>(null)
  const barChartRef = useRef<HTMLDivElement | null>(null)
  const [totalIv, setTotalIv] = useState<number>(0)
  const [usedIv, setUsedIv] = useState<number>(0)
  const labels = ["Chưa sử dụng", "Đã sử dụng"]
  const user = UserLoggedIn();
  useEffect(() => {
    const getInvoiceUsageData = async () => {
      const {data} = await DashboardCURD.getInvoiceUsage();
      const totalIv = user.store.total_bought_invoice ?? 0;
      const usedIv = data.data?.numOfpublishInv ?? 0;
      setUsedIv(usedIv)
      setTotalIv(totalIv)
    }
    getInvoiceUsageData();
  }, [store]);

  useEffect(() => {
    if (!pieChartRef.current) {
      return
    }

    const height = parseInt(getCSS(pieChartRef.current, 'height'))
    const remainIv = totalIv - usedIv;
    const series = [remainIv, usedIv];
    const pieChart = new ApexCharts(pieChartRef.current, getPieChartOptions(height, colorBase, series, labels, totalIv))
    const barChart = new ApexCharts(barChartRef.current, getBarChartOptions(height, colorBase, remainIv))
    if (pieChart) {
      pieChart.render()
    }
    if (barChart) {
      barChart.render()
    }

    return () => {
      if (pieChart) {
        pieChart.destroy()
      }
      if (barChart) {
        barChart.destroy()
      }
    }
  }, [totalIv])

  const emptyData = false;
  const baseColor = getCSSVariableValue(colorBase)
  const secondaryColor = getCSSVariableValue('--bs-success')

  return (
    <>
      <div className='col-lg-8'>
        <div className={`card ${className}`}>
          {/* begin::Header */}
          <div className='card-header border-0 pt-5'>
            <div className="card-title align-items-center d-flex justify-content-center">
              <h3>
                <span className='card-label fw-bolder fs-3 mb-1'>Hóa đơn điện tử</span>
              </h3>
            </div>
          </div>
          <div className='card-body pt-0'>
            {emptyData ?
              <div key={`item_key_${emptyData}`}
                  className="d-flex justify-content-center align-items-center text-muted fst-italic"
                  style={{height: '250px'}}>
                Không tìm thấy dữ liệu
              </div>
              :
              <div className='row' style={{alignItems: "center"}}>
                <div className='col-lg-7'>
                  <div ref={pieChartRef} id='kt_charts_widget_2_chart' style={{height: '250px'}}></div>
                </div>
                <div className='col-lg-5'>
                  <div className='mk-chart-data'>
                    <div className='mk-chart-labels'><span style={{backgroundColor: baseColor}}></span>Chưa sử dụng</div>
                    <div className='mk-chart-value' style={{color: baseColor}}>{formatPrice(parseInt((totalIv - usedIv).toFixed(0)))}</div>
                    <p className='text-muted'>Hóa đơn</p>
                  </div>
                  <div className='mk-chart-data'>
                    <div className='mk-chart-labels'><span style={{backgroundColor: secondaryColor}}></span>Đã sử dụng</div>
                    <div className='mk-chart-value' style={{color: secondaryColor}}>{formatPrice(parseInt(usedIv.toFixed(0)))}</div>
                    <p className='text-muted'>Hóa đơn</p>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
      <div className='col-lg-4'>
        <div className={`card ${className}`}>
          {/* begin::Header */}
          <div className='card-header border-0 pt-5'>
            <div className="card-title align-items-center d-flex justify-content-center">
              <h3>
                <span className='card-label fw-bolder fs-3 mb-1'>Số lượng HĐ đã mua</span>
              </h3>
            </div>
    
            {/* begin::Toolbar */}
            <div className='card-toolbar text-muted fst-italic' data-kt-buttons='true'>
              Đ/v: Hóa đơn
            </div>
          </div>
          <div className='card-body pt-0'>
            {emptyData ?
              <div key={`item_key_${emptyData}`}
                    className="d-flex justify-content-center align-items-center text-muted fst-italic"
                    style={{height: '250px'}}>
                Không tìm thấy dữ liệu
              </div>
              :
              <div ref={barChartRef} id='kt_charts_widget_3_chart' style={{height: '250px'}}></div>
            }
          </div>
        </div>
      </div>
    </>
  )
}

export {InvoiceCharts}

function getPieChartOptions(height: number, colorBase: string, series: number[], labels: string[], totalIv: number): ApexOptions {
  const baseColor = getCSSVariableValue(colorBase)
  const secondaryColor = getCSSVariableValue('--bs-success')
  return {
    series: series,
    chart: {
      fontFamily: 'inherit',
      type: 'donut',
      height: height,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            name: {
              show: true,
            },
            value: {
              show: true,
            },
            total: {
              show: true,
              showAlways: true,
              label: "Tổng HĐ đã mua",
              formatter: function (val) {
                let total = formatPrice(parseInt(totalIv.toFixed(0)))
                if (typeof total === 'number') {
                    total = total.toString();
                }
                return total;
              }
            }
          }
        }
      }
    },
    legend: {
      show: false,
    },
    labels: labels,
    fill: {
      opacity: 1,
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      // y: {
      //   formatter: function (val) {
      //     return val
      //   },
      // },
    },
    colors: [baseColor, secondaryColor]
  }
}

function getBarChartOptions(height: number, colorBase: string, remainIv: number): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue(colorBase)
  const secondaryColor = getCSSVariableValue('--bs-warning')
  const series = [{
    name: 'Hóa đơn',
    data: [remainIv],
  }];

  return {
    series,
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height: height,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '30%',
        borderRadius: 5,
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#FFF"]
      }
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: ['Hóa đơn'],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
        formatter: function(val) {
         let yLabel = formatPrice(parseInt(val.toFixed(0)))
         if (typeof yLabel === 'number') {
            yLabel = yLabel.toString();
         }
         return yLabel; // Example: Format to 2 decimal places
       }
      },
    },
    fill: {
      opacity: 1,
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      // y: {
      //   formatter: function (val) {
      //     return val
      //   },
      // },
    },
    colors: [baseColor, secondaryColor],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  }
}